import { useCallback } from 'react';

import { type UploadAnalyticsContext } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';
import { hasPausedUploadsSelector, isFinishedUploadingSelector } from '@air/redux-uploader';
import { useDeleteAllUploads, usePauseAllUploads, useResumeAllUploads } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { CancelAllUploadsModal } from '~/components/Modals/CancelAllUploads';
import { usePrivateUploadDelete } from '~/components/Upload/hooks/usePrivateUploadDelete';
import { useAirStore } from '~/utils/ReduxUtils';

export type UseCancelAllPrivateUploadsParams = {
  analyticsContext: UploadAnalyticsContext;
  deleteAllErrorContext: string;
};

export const useCancelAllPrivateUploads = ({
  analyticsContext,
  deleteAllErrorContext,
}: UseCancelAllPrivateUploadsParams) => {
  const { onDeleteUpload } = usePrivateUploadDelete({ analyticsContext });
  const { pauseAllUploads } = usePauseAllUploads();
  const { resumeAllUploads } = useResumeAllUploads();
  const { deleteAllUploads } = useDeleteAllUploads({ onDelete: onDeleteUpload });
  const [showCancelAllUploadsModal, closeCancelAllUploadsModal] = useAirModal(CancelAllUploadsModal);
  const store = useAirStore();

  const cancelAllUploads = useCallback(() => {
    const state = store.getState();
    const isFinished = isFinishedUploadingSelector(state);
    const hasPausedUploads = hasPausedUploadsSelector(state);
    const shouldConfirmBeforeCancel = !isFinished || hasPausedUploads;

    pauseAllUploads();

    const handleDeleteAllUploads = () => {
      deleteAllUploads().catch((error: unknown) => {
        reportErrorToBugsnag({ error, context: deleteAllErrorContext });
      });
    };

    if (shouldConfirmBeforeCancel) {
      showCancelAllUploadsModal({
        onContinueUpload: () => {
          resumeAllUploads();
        },
        onCancelUpload: () => {
          handleDeleteAllUploads();
          closeCancelAllUploadsModal();
        },
      });
    } else {
      handleDeleteAllUploads();
    }
  }, [
    closeCancelAllUploadsModal,
    deleteAllUploads,
    pauseAllUploads,
    resumeAllUploads,
    deleteAllErrorContext,
    showCancelAllUploadsModal,
    store,
  ]);

  return {
    cancelAllUploads,
  };
};
